// -----------------------------------------------------------------------------
// Contact page – hero responsive estilo ServicesPage + logo bajo botón WhatsApp
// -----------------------------------------------------------------------------

import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  Avatar,
  Snackbar,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  FiPhone,
  FiMail,
  FiMapPin,
  FiArrowUpRight,
  FiSend,
} from "react-icons/fi";
import { motion } from "framer-motion";
import { Map } from "pigeon-maps";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation } from "react-router-dom";

import heroImg from "../assets/eco-home.jpg";
import kwLogoKws from "../assets/logo/kw-logo-kws.png";

const ACCENT = "#FF8A00";
const SOFT = alpha(ACCENT, 0.07);

/* ---------- validación ---------- */
const schema = yup.object({
  first: yup.string().required("Nombre requerido"),
  last: yup.string().required("Apellidos requeridos"),
  email: yup.string().email().required("Correo requerido"),
  phone: yup
    .string()
    .matches(/^[0-9+()\s-]{8,}$/, "Teléfono inválido")
    .required("Teléfono requerido"),
  msg: yup.string().required("Cuéntanos tu proyecto"),
});

/* ---------- animaciones ---------- */
const rise = {
  hidden: { opacity: 0, y: 50 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.8 },
  }),
};

/* --------------------------------------------------------------------------- */
/* HERO                                                                        */
/* --------------------------------------------------------------------------- */
function Hero() {
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box
      sx={{
        height: { xs: "80vh", md: "100dvh" },
        minHeight: 420,
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(rgba(0,0,0,.55),rgba(0,0,0,.55)),url(${heroImg}) center/cover`,
        color: "#fff",
      }}
    >
      <Container>
        <motion.div variants={rise} initial="hidden" animate="visible">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Ethos Nova, sans-serif",
              fontWeight: 900,
              lineHeight: 1.08,
              fontSize: { xs: 34, sm: 42, md: 58 },
              maxWidth: 900,
              textShadow: "0 4px 14px rgba(0,0,0,.6)",
            }}
          >
            ¡Hablemos de tu&nbsp;
            <Box component="span" sx={{ color: ACCENT }}>
              energía&nbsp;solar
            </Box>
            !
          </Typography>
          <Typography
            variant={upMd ? "h6" : "subtitle1"}
            sx={{ mt: 2, maxWidth: 540, opacity: 0.9 }}
          >
            Agenda una asesoría gratuita y descubre cuánto podrías ahorrar cada
            mes en tu próximo recibo.
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
}

/* --------------------------------------------------------------------------- */
/* FORM COMPONENT                                                              */
/* --------------------------------------------------------------------------- */
function ContactForm({ register, errors, isSubmitting, onSubmit }) {
  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      noValidate
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 560 },
        bgcolor: "#fff",
        borderRadius: 4,
        p: { xs: 3, sm: 4, md: 5 },
        boxShadow: `0 12px 32px ${alpha("#000", 0.08)}`,
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          label="Nombre"
          {...register("first")}
          error={!!errors.first}
          helperText={errors.first?.message}
        />
        <TextField
          fullWidth
          label="Apellidos"
          {...register("last")}
          error={!!errors.last}
          helperText={errors.last?.message}
        />
      </Stack>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Correo electrónico"
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <TextField
          fullWidth
          label="Teléfono"
          {...register("phone")}
          error={!!errors.phone}
          helperText={errors.phone?.message}
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Mensaje"
          {...register("msg")}
          error={!!errors.msg}
          helperText={errors.msg?.message}
        />
        <Button
          type="submit"
          disabled={isSubmitting}
          startIcon={<FiSend />}
          sx={{
            alignSelf: "flex-start",
            bgcolor: ACCENT,
            color: "#fff",
            px: 5,
            py: 1.6,
            fontWeight: 700,
            borderRadius: "50px",
            textTransform: "none",
            "&:hover": { bgcolor: "#db6d00" },
          }}
        >
          Enviar mensaje
        </Button>
      </Stack>
    </Box>
  );
}

/* --------------------------------------------------------------------------- */
/* CONTACT SECTION                                                             */
/* --------------------------------------------------------------------------- */
function ContactSection() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema) });
  const [snack, setSnack] = useState(false);
  const send = (d) => {
    console.log(d);
    setSnack(true);
    reset();
  };

  return (
    <>
      <Box
        id="ready" /* 👈 ancla de destino */
        sx={{
          mt: { xs: 8, md: 12 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "flex-start" },
          justifyContent: "space-between",
          gap: { xs: 6, md: 8 },
        }}
      >
        {/* INFO */}
        <Stack
          spacing={3}
          sx={{ width: { xs: "100%", md: "50%" }, maxWidth: 560 }}
        >
          <Typography
            variant="h4"
            fontWeight={900}
            sx={{ fontFamily: "Ethos Nova, sans-serif" }}
          >
            ¿Listo para trabajar con&nbsp;nosotros?
          </Typography>

          <Typography variant="body1">
            En nuestra sección de Contacto te ofrecemos una puerta abierta hacia
            nuevas oportunidades. ¡Conversemos!
          </Typography>

          <Stack spacing={1.8} sx={{ pt: 1 }}>
            {[
              { icon: FiPhone, txt: "662 143 0033" },
              { icon: FiMail, txt: "info@kwsolar.com" },
              {
                icon: FiMapPin,
                txt: "Blvd Colosio C. Real del Arco, Hermosillo",
              },
            ].map(({ icon: Icon, txt }) => (
              <Stack key={txt} direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{ width: 34, height: 34, bgcolor: SOFT, color: ACCENT }}
                >
                  <Icon size={17} />
                </Avatar>
                <Typography fontWeight={500}>{txt}</Typography>
              </Stack>
            ))}
          </Stack>

          <Button
            href="https://wa.me/526621430033"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<FiArrowUpRight />}
            sx={{
              mt: 2,
              alignSelf: { xs: "center", md: "flex-start" },
              bgcolor: ACCENT,
              color: "#fff",
              px: 4,
              py: 1.4,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#db6d00" },
            }}
          >
            Escríbenos por WhatsApp
          </Button>

          {/* logo debajo */}
          <Box
            component="img"
            src={kwLogoKws}
            alt="KW Solar logo"
            sx={{
              width: { xs: 160, sm: 210 },
              mt: { xs: 3, md: 4 },
              opacity: 0.9,
              alignSelf: { xs: "center", md: "flex-start" },
            }}
          />
        </Stack>

        {/* FORM */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
          }}
        >
          <ContactForm
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit(send)}
          />
        </Box>
      </Box>

      <Snackbar
        open={snack}
        autoHideDuration={4000}
        onClose={() => setSnack(false)}
        message="¡Gracias! Te responderemos pronto."
      />
    </>
  );
}

/* --------------------------------------------------------------------------- */
/* MAP – visión general                                                        */
/* --------------------------------------------------------------------------- */
function MapBlock() {
  return (
    <Box
      sx={{
        mt: { xs: 10, md: 14 },
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: `0 10px 28px ${alpha("#000", 0.1)}`,
      }}
    >
      <Map
        height={360}
        defaultCenter={[29.073, -110.96]}
        defaultZoom={11}
        mouseEvents={false}
        touchEvents={false}
      />
    </Box>
  );
}

/* --------------------------------------------------------------------------- */
/* PAGE EXPORT                                                                 */
/* --------------------------------------------------------------------------- */
export default function ContactPage() {
  /* desplaza suavemente al abrir con #ready */
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  return (
    <Box component="main">
      <Hero />
      <Container maxWidth="lg" sx={{ pb: { xs: 10, md: 14 } }}>
        <ContactSection />
        <MapBlock />
      </Container>
    </Box>
  );
}
